import { ChangeEvent, useEffect, useState } from "react";

import classNames from "classnames";

import { Button, Input, Toggle } from "@vimeo/iris/components";
import { Text } from "@vimeo/iris/typography";
import { DismissX } from "@vimeo/iris/icons";

import { Spinner } from "@salesforce/design-system-react";

import * as axios from "axios";

import { CustomLogo, ListResponse, Picture, Video } from "vimeo";
import { updateEmbedSettings } from "./embedSettings";

import { client as vimeoClient } from "../../vimeoClient";

import { FileUpload } from "../../components/FileUpload";

import debounce from "lodash.debounce";

import "./customization.scoped.scss";
import "./common.scoped.scss";

interface Props {
    video: Video;
    onUpdate: (video: Video) => void;
    onError: (msg: string) => void;
}

const defaultColor = "00adef";

export function Customization(props: Props) {
    const { video, onUpdate, onError } = props;

    const [loading, setLoading] = useState(false);
    const [customLogos, setCustomLogos] = useState<Picture[]>();

    async function getCustomLogos() {
        const resp = await vimeoClient.get<ListResponse<Picture>>(
            "/api/vimeo/user/me/customlogos"
        );
        setCustomLogos(resp.data.data);
    }

    useEffect(() => {
        // We don't need to do anything if there is no active custom logo for this video.
        if (!video.embed.logos.custom?.active) {
            return;
        }

        getCustomLogos()
            .catch(() => onError("Failed to logo custom logos."))
            .then(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [video.embed.logos.custom?.active]);

    async function changeCustomColor(color: string) {
        setLoading(true);
        try {
            const updatedVideo = await updateEmbedSettings(video, {
                color,
            });

            onUpdate(updatedVideo);
        } catch (err) {
            onError("Failed to save changes. Please try again later.");
        }

        setLoading(false);
    }

    async function onCustomColorChange(e: ChangeEvent) {
        const value = (e.target as HTMLInputElement).value;

        await changeCustomColor(value.replace("#", ""));
    }

    async function onShowVimeoLogoToggle(e: ChangeEvent) {
        setLoading(true);
        try {
            const checked = (e.target as HTMLInputElement).checked;

            const updatedVideo = await updateEmbedSettings(video, {
                logos: {
                    vimeo: checked,
                    custom: {
                        active: false,
                    },
                },
            });

            onUpdate(updatedVideo);
        } catch (err) {
            onError("Failed to save changes. Please try again later.");
        }
        setLoading(false);
    }

    async function updateCustomLogoSettings(custom: Partial<CustomLogo>) {
        setLoading(true);
        try {
            const updatedVideo = await updateEmbedSettings(video, {
                logos: {
                    custom,
                    vimeo: false,
                },
            });

            onUpdate(updatedVideo);
        } catch (err) {
            onError("Failed to save changes. Please try again later.");
        }
        setLoading(false);
    }

    async function onCustomLogoToggle(e: ChangeEvent) {
        // If there was already a previous custom logo, then simply re-activate it.
        // Otherwise, the user has to upload a logo which will be activated
        // upon upload completion that is handled separately as part of the
        // upload process.
        if (!video.embed.logos.custom) {
            return;
        }

        const checked = (e.target as HTMLInputElement).checked;
        await updateCustomLogoSettings({ active: checked });
    }

    async function onStickyCustomLogoToggle(e: ChangeEvent) {
        const checked = (e.target as HTMLInputElement).checked;
        await updateCustomLogoSettings({ sticky: checked });
    }

    async function onCustomLogoLinkChange(e: ChangeEvent) {
        const value = (e.target as HTMLInputElement).value;
        // If the value is non-empty, then validate the URL.
        if (value) {
            try {
                new URL(value);
            } catch (err) {
                // Ignore parsing errors and just return.
                return;
            }
        }
        await updateCustomLogoSettings({ link: value });
    }

    async function onCustomLogoChange(e: ChangeEvent) {
        setLoading(true);
        const value = (e.target as HTMLInputElement).value;
        await updateCustomLogoSettings({ active: true, url: value });
        setLoading(false);
    }

    async function onDeleteCustomLogoClicked(uri: string) {
        setLoading(true);
        await vimeoClient.delete(`/api/vimeo${uri}`);
        await getCustomLogos();
        setLoading(false);
    }

    function getPlayerCustomColor(): string {
        return video.embed.color ? `#${video.embed.color}` : `#${defaultColor}`;
    }

    // Don't show the delete button if there is only one custom
    // logo. This matches the behavior on vimeo.com.
    const lastCustomLogo = customLogos?.length === 1;
    const customLogoEls = customLogos?.map((cl, index) => (
        <div
            key={index}
            className={classNames([
                "slds-col",
                "slds-size_1-of-3",
                "custom-logo-picker",
                "slds-var-p-around_small",
                "slds-var-m-left_small",
                "slds-var-m-bottom_small",
                { active: cl.base_link === video.embed.logos.custom?.url },
            ])}
        >
            <input
                type="radio"
                id={`picker-${index}`}
                name="customLogo"
                value={cl.base_link}
                onChange={onCustomLogoChange}
                hidden
            />
            <label htmlFor={`picker-${index}`}>
                <img className="custom-logo" src={cl.sizes[0].link} alt="" />
            </label>
            {!lastCustomLogo && (
                <div
                    className="slds-var-m-top_small"
                    style={{
                        display: "inline-block",
                        position: "absolute",
                        top: "25px",
                    }}
                >
                    <Button
                        icon={<DismissX color="#000000" />}
                        format="secondary"
                        onClick={() => onDeleteCustomLogoClicked(cl.uri)}
                        size="xs"
                    />
                </div>
            )}
        </div>
    ));

    async function onFileSelected(e: ChangeEvent) {
        const files = (e.target as HTMLInputElement).files;
        if (!files || !files.length) {
            return;
        }

        const file = files[0];
        if (file.type !== "image/png" && file.type !== "image/jpg") {
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append("data", file);

            const uploadResp = await axios.default.post<Picture>(
                `/api/vimeo/user/me/customlogos?imageType=${file.type}`,
                formData,
                {
                    headers: {
                        Accept: "application/vnd.vimeo.*+json;version=3.4",
                        "Content-Length": file.size,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            await getCustomLogos();
            await updateCustomLogoSettings({
                active: true,
                url: uploadResp.data.base_link,
            });
        } catch (err) {
            console.log("Failed to upload image", err);
            onError("Failed to upload image. Please try again later.");
        }

        setLoading(false);
    }

    return (
        <>
            {loading && <Spinner />}
            <Text size={300} style={{ fontWeight: 500 }}>
                Customization
            </Text>
            <div className="toggles-container">
                <div className="slds-form-element">
                    <label className="slds-checkbox_toggle slds-grid slds-grid_align-spread">
                        <span className="slds-form-element__label">
                            <Text size={200}>Customize color</Text>
                        </span>
                        <span
                            id="customize-color-toggle"
                            className="slds-checkbox_faux_container"
                            aria-live="assertive"
                        >
                            <Text size={200}>{getPlayerCustomColor()}</Text>
                            <input
                                className="slds-var-m-left_small"
                                type="color"
                                value={getPlayerCustomColor()}
                                onChange={onCustomColorChange}
                            />
                        </span>
                    </label>
                </div>
                <div className="slds-form-element">
                    <div className="slds-form-element__control">
                        <Toggle
                            label="Show Vimeo logo"
                            checked={video.embed.logos.vimeo}
                            onChange={onShowVimeoLogoToggle}
                        />
                    </div>
                </div>
                <div className="slds-form-element">
                    <div className="slds-form-element__control">
                        <Toggle
                            label="Display custom logo"
                            checked={video.embed.logos.custom?.active}
                            onChange={onCustomLogoToggle}
                        />
                    </div>
                    {video.embed.logos.custom?.active && (
                        <div className="slds-grid slds-wrap slds-gutters slds-var-m-vertical_small">
                            {customLogoEls}
                        </div>
                    )}
                    <FileUpload
                        onChange={onFileSelected}
                        mimeTypes="image/png,image/jpg"
                    />
                </div>
                <div className="slds-form-element">
                    <div className="slds-form-element__control">
                        <Toggle
                            label="Show custom logo during playback"
                            checked={video.embed.logos.custom?.sticky}
                            disabled={!video.embed.logos.custom?.active}
                            onChange={onStickyCustomLogoToggle}
                        />
                    </div>
                </div>
                <div className="slds-form-element">
                    <div className="slds-form-element__control">
                        <Input
                            type="url"
                            placeholder="https://www.mywebsite.com"
                            disabled={!video.embed.logos.custom?.active}
                            defaultValue={video.embed.logos.custom?.link}
                            onChange={debounce(onCustomLogoLinkChange, 300)}
                            label="Custom logo link"
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
